import React from 'react';
import {
  Component,
  Expressions,
  getSystem,
  matchExpression,
  Plugins,
  TypeImplementation,
  Type,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {AddIcon} from '@packages/systems/spring';
import {
  EElement,
  EList,
  ELiteral,
  ERecord,
  EText,
  type ExpressionElement,
  TElement,
  TList,
} from '@packages/systems/wfdl-ast';
import {useTheme} from '@packages/utilities/styled';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {BaseButton as Button} from '@packages/systems/design-system/Button';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';
import * as StringUtils from '@packages/utilities/string-utils';
import {concat, constant, notNil} from '@packages/utilities/fp/utils';
import {getChildren, findFurthestAncestor} from '@packages/utilities/platform';
import {isNavbarWrapper} from '@packages/utilities/elementTypeUtils';
import {
  MENU_STATE_HIDE,
  MENU_STATE_SHOW,
  TEMP_NAVBAR_MENU_PATH,
} from '@packages/utilities/expression-path-constants';
import {getNodeClickedActionCreatorFromState} from '@packages/systems/designer/canvas';
import {
  selectSelectedElement,
  selectCurrentComponent,
} from '@packages/utilities/platform-selectors';
import {NAVBAR_MENU_OPEN_TOGGLED} from '@packages/systems/designer/actions';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerStores,
} from '@packages/systems/designer/flux';

type System = {
  label: string;
  labelVariant: 'hidden';
};
const navbarLink = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarLink')
);
const stringType = qualify(Plugins.name('Basic'), Component.name('String'));

const getLinkTexts = matchExpression({
  Element: (val) => {
    if (namesEqual(val.type, navbarLink)) {
      const children = getChildren(val);
      if (children && children[0] && namesEqual(children[0].type, stringType))
        return Expressions.stop([Expressions.getText(children[0].data)]);
    }
    return Expressions.stop([]);
  },
  _: constant(Expressions.resume([])),
});

const validateTypeInDevelopment = (
  expr: ContextualExpression,
  type: null | Type
): System => {
  if (type === null) {
    throw Error('Expected type from AnnotatedExpression to be defined');
  }
  const system = getSystem(type);

  if (process.env.NODE_ENV === 'development') {
    if (system === null) {
      throw Error('Expected system from AnnotatedExpression to be defined');
    }

    if (!system.hasOwnProperty('label')) {
      throw Error('label is required');
    }

    if (
      !system.hasOwnProperty('labelVariant') ||
      system.labelVariant !== 'hidden'
    ) {
      throw Error('labelVariant is required to be `hidden`');
    }
  }
  return {label: (system && system.label) || '', labelVariant: 'hidden'};
};

const generateNavbarLink = (label: string, id: string) =>
  getSecond(
    instantiateExpressionWithUniqueIds(
      EElement({
        id: 'Navbar Link',
        type: qualify(Plugins.name('Navbar'), Component.name('NavbarLink')),
        data: ERecord({
          navbar: ERecord({
            type: EText('link'),
          }),
          link: ELiteral({
            name: qualify(Plugins.name('Basic'), typeName('Link')),
            value: {
              mode: 'external',
              url: '#',
            },
          }),
          children: EList([
            EElement({
              id: 'String',
              type: qualify(Plugins.name('Basic'), Component.name('String')),
              data: EText(label),
            }),
          ]),
        }),
      })
    )(id)
  );

const menuIsOpen = (navbarWrapper: ExpressionElement): boolean =>
  Expressions.getElementDataAt(
    navbarWrapper,
    TEMP_NAVBAR_MENU_PATH,
    'Enum',
    MENU_STATE_HIDE
  ) === MENU_STATE_SHOW;

const getCollapseValue = (navbarWrapper: ExpressionElement) =>
  Expressions.getElementDataAt(
    navbarWrapper,
    [
      {in: 'Record', at: 'navbar'},
      {in: 'Record', at: 'collapse'},
    ],
    'Text',
    'medium'
  );

const selectStyleBlockStore = (state: DesignerStores) => state.StyleBlockStore;
const selectExternalStore = (state: DesignerStores) => state.ExternalStore;

const AddNavbarLink = TypeImplementation.Alias({
  type: TList(TElement),
  render: function AddNavbarLinkRender({expr, dispatch}) {
    const flux = useFluxContext();
    const nodeClicked = getNodeClickedActionCreatorFromState(flux.state);
    const {gutters, control} = useTheme();
    const wfDispatch = useDispatch();
    const currentComponent = useDesignerSelector(selectCurrentComponent);
    const styleBlockState = useDesignerSelector(selectStyleBlockStore);
    const externalState = useDesignerSelector(selectExternalStore);
    const selectedElement = useDesignerSelector(selectSelectedElement);
    const mediaQueryList = styleBlockState.getOrderedBreakpointIds();

    const {type} = getAnn(expr);
    const system = validateTypeInDevelopment(expr, type);
    const strippedExpr = stripAll(expr);

    const rootElement = Component.getRender(currentComponent);

    /**
     * Dynamically adds a NavbarLink to the NavbarMenu
     */
    const addLink = React.useCallback(() => {
      // Find path to NavbarMenu
      const navbarMenuPath = Expressions.findPaths(
        strippedExpr,
        matchExpression({
          Element: (e) =>
            namesEqual(
              qualify(Plugins.name('Navbar'), Component.name('NavbarMenu')),
              e.type
            ),
          _: () => false,
        })
      )[0];

      if (!navbarMenuPath) {
        throw Error(
          'Unable to get `navbarMenuPath` from Navbar Annotated Expression Element'
        );
      }

      // Path to NavbarMenu's children
      const navbarMenuPathToChildren: ExpressionPath = [
        ...navbarMenuPath,
        {in: 'Element'},
        {in: 'Record', at: 'children'},
      ];

      // Retrieve the NavbarMenu's children
      const navbarMenuChildren = Expressions.getIn(
        strippedExpr,
        navbarMenuPathToChildren
      );

      if (!navbarMenuChildren) {
        throw Error(
          'Unable to get `navbarMenuChildren` Expression from Navbar Annotated Expression Element'
        );
      }

      const id = generateGuid();

      // Do the walk and retrieve NavbarLink labels in order to increment below
      const otherLabels = Expressions.everything(concat)(getLinkTexts)(
        navbarMenuChildren
      )
        .filter(notNil)
        .map(String);

      // Generate a new NavbarLink with an incremented label (i.g: Link 4)
      const newNavbarLink =
        otherLabels &&
        generateNavbarLink(StringUtils.increment(otherLabels, 'Link'), id);

      // Update the main List expression we're working with to include our new NavbarLink
      const updatedExpr = Expressions.updateIn(
        strippedExpr,
        navbarMenuPathToChildren,
        (list) => {
          const navbarLinks = Expressions.getList(list);
          if (navbarLinks) {
            return EList([...navbarLinks, newNavbarLink]);
          }
          throw Error('No `navbarLinks` found');
        }
      );

      // Open the current `NavbarWrapper` menu if it's not already open and
      // it's within selected mediaQuery and collapse range.
      if (selectedElement) {
        const navbarWrapper = isNavbarWrapper(selectedElement)
          ? selectedElement
          : findFurthestAncestor(rootElement, selectedElement, isNavbarWrapper);

        if (navbarWrapper) {
          const collapseValue = getCollapseValue(navbarWrapper);
          // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'BreakpointID'.
          const collapseIndex = mediaQueryList.indexOf(collapseValue);
          const mqIndex = mediaQueryList.indexOf(externalState.mediaQuery);

          if (!menuIsOpen(navbarWrapper) && mqIndex >= collapseIndex) {
            wfDispatch({
              type: NAVBAR_MENU_OPEN_TOGGLED,
              payload: {
                navbarWrapper: navbarWrapper.id,
                mediaQueryList,
              },
            });
          }
        }
      }

      dispatch(updateExpressionAction(updatedExpr));

      wfDispatch(
        nodeClicked({
          nativeIdInCurrentComponent: id,
        })
      );
    }, [
      nodeClicked,
      dispatch,
      externalState.mediaQuery,
      mediaQueryList,
      rootElement,
      selectedElement,
      strippedExpr,
      wfDispatch,
    ]);

    return (
      <Hitarea>
        <Button
          data-automation-id="AddNavbarLink"
          onClick={addLink}
          style={{
            gap: gutters.base,
            height: control.height,
          }}
        >
          <AddIcon />
          <span>{system.label}</span>
        </Button>
      </Hitarea>
    );
  },
});

export default AddNavbarLink;
